import { useMemo, useState } from "react";
import { GROW_CATEGORIES, GROW_IDEAS } from "@/lib/grow-ideas";
import {
  Rocket,
  Printer,
  Clock,
  Zap,
  Target,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const EFFORT_LABEL = {
  low: { label: "Low effort", color: "text-green-700 bg-green-50 border-green-200" },
  med: { label: "Medium effort", color: "text-amber-700 bg-amber-50 border-amber-200" },
  high: { label: "High effort", color: "text-red-700 bg-red-50 border-red-200" },
};

const TIME_LABEL = {
  "this-week": { label: "This week", color: "bg-black text-white" },
  "this-month": { label: "This month", color: "bg-neutral-800 text-white" },
  "3-months": { label: "3-month project", color: "bg-neutral-600 text-white" },
  seasonal: { label: "Seasonal", color: "bg-amber-600 text-white" },
};

const IdeaCard = ({ idea }) => {
  const [open, setOpen] = useState(false);
  const eff = EFFORT_LABEL[idea.effort] || EFFORT_LABEL.med;
  const tim = TIME_LABEL[idea.time] || TIME_LABEL["this-month"];
  return (
    <div
      className="swiss-card break-inside-avoid"
      data-testid={`grow-idea-${idea.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 border ${eff.color}`}
            >
              {eff.label}
            </span>
            <span
              className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${tim.color}`}
            >
              {tim.label}
            </span>
          </div>
          <h3 className="font-display font-extrabold text-lg leading-tight">
            {idea.title}
          </h3>
          <p className="text-sm text-neutral-700 mt-2 leading-snug">
            {idea.desc}
          </p>
          <div className="mt-3 flex items-start gap-2">
            <Target className="h-4 w-4 mt-0.5 text-green-700 flex-shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-green-700">
                Revenue potential
              </div>
              <div className="text-sm font-bold text-green-800">
                {idea.revenue}
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="mt-4 text-[11px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black inline-flex items-center gap-1"
        data-testid={`grow-toggle-${idea.id}`}
      >
        {open ? (
          <>
            Hide action steps <ChevronUp className="h-3.5 w-3.5" />
          </>
        ) : (
          <>
            Show me how <ChevronDown className="h-3.5 w-3.5" />
          </>
        )}
      </button>
      {open && (
        <ol className="mt-3 space-y-2 text-sm text-neutral-700 border-l-4 border-l-indigo-500 pl-4">
          {idea.steps.map((s, i) => (
            <li key={i} className="leading-snug">
              <span className="font-bold mr-1">{i + 1}.</span> {s}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const GrowPage = () => {
  const [activeCat, setActiveCat] = useState("all");

  const filtered = useMemo(
    () =>
      activeCat === "all"
        ? GROW_IDEAS
        : GROW_IDEAS.filter((i) => i.category === activeCat),
    [activeCat],
  );

  const totalRevPotential = GROW_IDEAS.length;
  const quickWinCount = GROW_IDEAS.filter(
    (i) => i.time === "this-week",
  ).length;

  return (
    <div data-testid="grow-page" className="fade-rise">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="swiss-label mb-3 flex items-center gap-2">
            <Rocket className="h-3.5 w-3.5" /> Grow
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
            Make more. Chase less.
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            {totalRevPotential} proven revenue plays for outdoor blinds &
            small-trade retail. Curated from what actually works in this
            industry — no fluff, no &quot;hustle culture&quot; nonsense.{" "}
            <span className="font-bold">
              {quickWinCount} can be done this week.
            </span>
          </p>
        </div>
        <button
          data-testid="grow-print-btn"
          onClick={() => window.print()}
          className="border-2 border-black bg-white text-black hover:bg-black hover:text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 inline-flex items-center gap-2 print:hidden"
        >
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      {/* Category filter */}
      <section
        className="flex flex-wrap gap-2 mb-6 print:hidden"
        data-testid="grow-categories"
      >
        <button
          data-testid="grow-cat-all"
          onClick={() => setActiveCat("all")}
          className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
            activeCat === "all"
              ? "border-black bg-black text-white"
              : "border-neutral-300 text-neutral-700 hover:border-black hover:text-black"
          }`}
        >
          All ({GROW_IDEAS.length})
        </button>
        {GROW_CATEGORIES.map((cat) => {
          const count = GROW_IDEAS.filter((i) => i.category === cat.id).length;
          return (
            <button
              key={cat.id}
              data-testid={`grow-cat-${cat.id}`}
              onClick={() => setActiveCat(cat.id)}
              className={`border-2 px-3 py-2 text-[11px] uppercase tracking-widest font-bold transition-colors inline-flex items-center gap-1.5 ${
                activeCat === cat.id
                  ? "border-black bg-black text-white"
                  : "border-neutral-300 text-neutral-700 hover:border-black hover:text-black"
              }`}
            >
              <span>{cat.icon}</span>
              {cat.label} ({count})
            </button>
          );
        })}
      </section>

      {/* Active category description */}
      {activeCat !== "all" && (
        <div
          className="mb-4 border-l-4 border-l-black bg-neutral-50 px-4 py-2"
          data-testid="grow-cat-desc"
        >
          <div className="text-sm text-neutral-700">
            <span className="font-bold">
              {GROW_CATEGORIES.find((c) => c.id === activeCat)?.label}:
            </span>{" "}
            {GROW_CATEGORIES.find((c) => c.id === activeCat)?.desc}
          </div>
        </div>
      )}

      {/* Ideas grid */}
      <section
        className="grid grid-cols-1 md:grid-cols-2 gap-5"
        data-testid="grow-grid"
      >
        {filtered.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} />
        ))}
      </section>

      {/* Footer prompt */}
      <section
        className="swiss-card mt-8 border-l-4 border-l-indigo-500"
        data-testid="grow-footer"
      >
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div>
            <div className="font-display font-extrabold text-lg leading-snug">
              Pick ONE and start today.
            </div>
            <div className="text-sm text-neutral-700 mt-1 leading-snug">
              The tradies who grow aren&apos;t the ones who read the most ideas —
              they&apos;re the ones who pick one, do it this week, then pick the
              next. Start with a{" "}
              <span className="font-bold">quick win</span> — 30 minutes of work
              and you&apos;ll see the return in Billy inside a fortnight.
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-neutral-500">
              <Clock className="h-3.5 w-3.5" /> Bookmark this page. Come back
              once a month.
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default GrowPage;
